import {
  type AccountInfo,
  type AuthenticationResult,
  EventType,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { useAccount, useMsal } from '@azure/msal-react';
import { clearTransientSignInRetry } from '@shared/auth/transientSignInRetry';
import type { AxiosEventBus } from '@shared/axios/axiosEventBus';
import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

import createHttpClient from '@/api/client';
import type { TenantResponse, UserResponse } from '@/api/schema/data-contracts';
import { Tenant } from '@/api/schema/Tenant';
import { User } from '@/api/schema/User';
import { useWatchEffect } from '@/hooks/watchEffect';
import { reportCrash } from '@/services/crashReporting';
import { updateYbugSettings } from '@/services/yBug';

export type IAuthContext = {
  auth: AuthenticationResult | null;
  tenant: TenantResponse | null;
  user: UserResponse | null;
  account: AccountInfo | null;
  /** Set when sign-in itself failed, so the shell can stop waiting and say so (EWM-1030). */
  authError: Error | null;
  logout: () => void;
  refreshTenant: () => Promise<void>;
};

// Exported so Storybook can supply a signed-in tenant/user; the real provider needs MSAL.
export const AuthContext = createContext<IAuthContext>({
  account: null,
  tenant: null,
  user: null,
  auth: null,
  authError: null,
  logout: () => {},
  refreshTenant: async () => {},
});

export const AuthProvider: FCC<Pick<AxiosEventBus, 'emit'>> = ({ children, emit }) => {
  const [authContext, setAuthContext] = useState<
    Pick<IAuthContext, 'tenant' | 'auth' | 'user' | 'authError'>
  >({
    tenant: null,
    auth: null,
    user: null,
    authError: null,
  });

  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  // Renew well before expiry. Background tabs throttle setTimeout (often to >=1 min),
  // so a 1-minute lead frequently fires after the token has already expired, producing
  // 401s that silently strip permission-gated UI until a manual reload (EWM-653).
  const minutesBeforeTokenRenewal = 5;

  const logoutCallback = useCallback(() => {
    instance
      .logoutRedirect({
        account: account,
        idTokenHint: account?.idToken,
      })
      .catch((e) => {
        console.error('Unexpected', e);
      });
  }, [instance, account]);

  useLayoutEffect(() => {
    let tokenRenewalTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const callbackId = instance.addEventCallback((event) => {
      const { eventType, payload } = event;

      switch (eventType) {
        case EventType.LOGIN_SUCCESS:
        case EventType.ACQUIRE_TOKEN_SUCCESS: {
          const auth = payload as AuthenticationResult;

          instance.setActiveAccount(auth.account);

          // EWM-960: never publish a payload without an access token. A redirect login fires
          // LOGIN_SUCCESS with none - it arrives on the ACQUIRE_TOKEN_SUCCESS that follows -
          // and every consumer reads the token off this context, so publishing it made them
          // all send `Bearer undefined`. Leaving auth null until a real token exists also
          // keeps RouterProvider's `auth ? ... : ...` key on 'unauthenticated', so the
          // authenticated tree mounts once, with a usable token, instead of mounting against
          // a broken one and never refetching (shouldRetryQuery does not retry 4xx).
          if (!auth.accessToken) {
            break;
          }

          // No POST /api/v1/auth/login here (EWM-949). The LOGIN_SUCCESS payload carries no
          // accessToken on a redirect login (that arrives on ACQUIRE_TOKEN_SUCCESS), so the
          // call went out as `Bearer undefined` and 401'd on every single sign-in. It was also
          // redundant: AuthenticationValidationClaimsTransformation runs the same
          // AuthenticationService.Validate (get-or-create user, tenant and permission claims)
          // on every authenticated request, so provisioning happens on the next call anyway.

          // for now leave all diagnostics in place, later we can comment them out
          if (auth.expiresOn) {
            // eslint-disable-next-line no-console
            console.debug('Token expires at:', auth.expiresOn);
            const renewalDate = new Date(
              auth.expiresOn.setMinutes(auth.expiresOn.getMinutes() - minutesBeforeTokenRenewal),
            );
            const renewalInterval = renewalDate.getTime() - Date.now();
            // eslint-disable-next-line no-console
            console.debug(
              'Token renewal scheduled in %s minutes (at %s)',
              new Date(renewalInterval).getMinutes(),
              renewalDate,
            );

            tokenRenewalTimeoutId = setTimeout(() => {
              // eslint-disable-next-line no-console
              console.debug('Token renewal scheduled, renewing...');
              const renewalRequest = {
                account: auth.account,
                scopes: auth.scopes,
              };
              instance
                .acquireTokenSilent({ ...renewalRequest, forceRefresh: true })
                .then((result) => {
                  // eslint-disable-next-line no-console
                  console.debug('Silent token renewed:', result);
                  instance.setActiveAccount(result.account);
                  setAuthContext((prev) => ({ ...prev, auth: result }));
                })
                .catch((error) => {
                  if (error instanceof InteractionRequiredAuthError) {
                    instance
                      .acquireTokenPopup(renewalRequest)
                      .then((result) => {
                        // eslint-disable-next-line no-console
                        console.debug('Interactive token renewed:', result);
                        instance.setActiveAccount(result.account);
                        setAuthContext((prev) => ({ ...prev, auth: result }));
                      })
                      .catch((e) => {
                        console.error(e);
                      });
                  }
                  console.error('Token renewal failed:', error);
                });
            }, renewalInterval);
          }

          // EWM-1136: sign-in worked, so give the next session its one transient retry back.
          clearTransientSignInRetry();
          setAuthContext((prev) => ({ ...prev, auth, authError: null }));
          break;
        }
        // EWM-1030: msal-browser v5 has no LOGIN_FAILURE - a failed redirect sign-in surfaces
        // here. Handling nothing was the defect the user reported as "no feedback": the splash
        // screen in InitializationProvider only clears on success, so a rejected
        // handleRedirectPromise left the user on "Initializing system" indefinitely while the 5s
        // force-login timer bounced them back to B2C. It was invisible to us too - AppExceptions
        // held nothing for either STAGE report.
        //
        // Only bootstrap failures surface. With an account already cached this is a background
        // renewal, which the branch above already recovers from via acquireTokenSilent/Popup;
        // surfacing that would blank a working session.
        case EventType.ACQUIRE_TOKEN_FAILURE: {
          if (instance.getAllAccounts().length > 0) {
            break;
          }

          const failure = event.error instanceof Error ? event.error : new Error('Sign-in failed');
          reportCrash(failure, {
            source: 'auth-failure',
            interactionType: event.interactionType,
            correlationId: event.correlationId,
          });
          setAuthContext((prev) => ({ ...prev, authError: failure }));
          break;
        }
        case EventType.LOGOUT_SUCCESS: {
          instance.setActiveAccount(null);
          setAuthContext((prev) => ({ ...prev, auth: null, authError: null }));
          clearTimeout(tokenRenewalTimeoutId);
          break;
        }
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
      clearTimeout(tokenRenewalTimeoutId);
    };
  }, [instance]);

  const refreshTenant = useCallback(async () => {
    const { auth } = authContext;
    if (!auth) {
      // eslint-disable-next-line no-console
      console.warn('Cannot refresh tenant: no auth token available');
      return;
    }

    try {
      const { v1TenantList } = new Tenant(
        createHttpClient<unknown>(
          {
            securityWorker: () => ({
              headers: {
                Authorization: `Bearer ${auth.accessToken}`,
              },
            }),
          },
          emit,
        ),
      );

      const { data: tenantResponse } = await v1TenantList();
      setAuthContext((prev) => ({ ...prev, tenant: tenantResponse }));
    } catch (error) {
      console.error('Failed to refresh tenant:', error);
      // Don't throw - allow the app to continue with stale data
      // The user can refresh the page if needed
    }
  }, [authContext, emit]);

  useWatchEffect(() => {
    const { auth, tenant } = authContext;
    if (auth && !tenant) {
      void (async () => {
        const { v1TenantList } = new Tenant(
          createHttpClient<unknown>(
            {
              securityWorker: () => ({
                headers: {
                  Authorization: `Bearer ${auth.accessToken}`,
                },
              }),
            },
            emit,
          ),
        );

        const { v1UsersCurrentList } = new User(
          createHttpClient<unknown>(
            {
              securityWorker: () => ({
                headers: {
                  Authorization: `Bearer ${auth.accessToken}`,
                },
              }),
            },
            emit,
          ),
        );

        const { data: currentUserResponse } = await v1UsersCurrentList();
        const { data: tenantResponse } = await v1TenantList();
        setAuthContext((prev) => ({ ...prev, tenant: tenantResponse, user: currentUserResponse }));
      })();
    }
  }, [authContext, emit]);

  // Update Ybug settings when user or tenant changes
  useWatchEffect(() => {
    updateYbugSettings(authContext.user, authContext.tenant);
  }, [authContext.user, authContext.tenant]);

  return (
    <AuthContext.Provider
      value={useMemo<IAuthContext>(
        () => ({
          ...authContext,
          account,
          logout: logoutCallback,
          refreshTenant,
        }),
        [authContext, account, logoutCallback, refreshTenant],
      )}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
